import { useEffect, useState, useRef, Component } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  InputNumber,
  Select,
  DatePicker,
  Tag,
  message,
  Row,
  Col,
  Card,
  Statistic,
  Input,
  Tabs,
  Tooltip,
} from 'antd'
import {
  SearchOutlined,
  DollarOutlined,
  WarningOutlined,
  SaveOutlined,
  HistoryOutlined,
  SyncOutlined,
  EditOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import type { ColumnsType } from 'antd/es/table'
import type { InputRef } from 'antd'
import { confirmDanger } from '../../hooks/useConfirm'
import { priceAPI, categoryAPI } from '../../api'

const { Option } = Select

interface Category {
  id: number
  name: string
  level: number
  parentId: number | null
}

interface Sku {
  id: number
  productId: number
  productName: string
  spec: string
  price: number
  stock: number
  stockWarning: number
  promotionId?: number
  promotionName?: string
  productionDate?: string
  expiryDate?: string
  nearExpiry?: boolean
  categoryId: number
  categoryName: string
  isActive: boolean
  _modified?: boolean
  _originalPrice?: number
  _originalStock?: number
  _originalPromotionId?: number
  _originalProductionDate?: string
  _originalNearExpiry?: boolean
}

interface Promotion {
  id: number
  name: string
  type: string
  discountValue: number
}

interface PriceHistory {
  id: number
  skuId: number
  oldPrice: number
  newPrice: number
  changeTime: string
  operator: string
}

class PageErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: any) { super(props); this.state = { hasError: false } }
  static getDerivedStateFromError() { return { hasError: true } }
  componentDidCatch() {}
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 15, marginBottom: 16 }}>页面出现异常，点击下方按钮即可恢复，已编辑未保存的数据请重新录入。</p>
          <Button type="primary" onClick={() => window.location.reload()}>刷新页面</Button>
        </div>
      )
    }
    return this.props.children
  }
}

interface EditableCellProps {
  title: React.ReactNode
  editable: boolean
  children: React.ReactNode
  dataIndex: string
  record: Sku
  editing: boolean
  inputType: 'number' | 'select' | 'date' | 'text'
  options?: { value: number; label: string }[]
  onSave: (record: Sku, dataIndex: string, value: any) => void
}

const EditableCell: React.FC<EditableCellProps> = ({
  title, editable, children, dataIndex, record, editing, inputType, options, onSave, ...restProps
}) => {
  const [editingCell, setEditingCell] = useState(false)
  const inputRef = useRef<InputRef>(null)
  const [value, setValue] = useState<any>()
  // Use ref to always access latest value (avoids stale closure in onBlur/onChange race)
  const valueRef = useRef<any>(undefined)
  const saveTimerRef = useRef<any>(null)
  const savedRef = useRef(false)

  useEffect(() => {
    valueRef.current = value
  }, [value])

  // On unmount, flush any unsaved value
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      if (!savedRef.current && valueRef.current !== undefined) {
        onSave(record, dataIndex, valueRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (editingCell && inputRef.current) inputRef.current.focus()
  }, [editingCell])

  const handleDoubleClick = () => {
    if (!editable) return
    const current = record[dataIndex as keyof Sku]
    setValue(current)
    valueRef.current = current
    savedRef.current = false
    setEditingCell(true)
  }

  const doSave = (val: any) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    onSave(record, dataIndex, val)
    savedRef.current = true
    setEditingCell(false)
  }

  const handleSave = () => {
    doSave(valueRef.current)
  }

  const scheduleSave = (val: any) => {
    setValue(val)
    valueRef.current = val
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => doSave(val), 150)
  }

  const renderInput = () => {
    if (inputType === 'number') {
      return (
        <InputNumber ref={inputRef as any} value={value}
          onChange={(val) => { setValue(val); valueRef.current = val }}
          onPressEnter={handleSave} onBlur={handleSave} min={0}
          step={dataIndex === 'price' ? 0.01 : 1}
          precision={dataIndex === 'price' ? 2 : 0} style={{ width: '100%' }} />
      )
    }
    if (inputType === 'select' && options) {
      return (
        <Select
          ref={inputRef as any}
          value={value}
          onChange={(val) => scheduleSave(val)}
          style={{ width: '100%' }}
          autoFocus
          defaultOpen
          optionFilterProp="children"
        >
          {options.map(opt => (
            <Option key={String(opt.value)} value={opt.value as any}>{opt.label}</Option>
          ))}
        </Select>
      )
    }
    if (inputType === 'date') {
      return (
        <DatePicker
          ref={inputRef as any}
          value={value && value !== 'null' ? dayjs(value) : null}
          onChange={(date) => scheduleSave(date ? date.format('YYYY-MM-DD') : null)}
          style={{ width: '100%' }}
          autoFocus
          defaultOpen
          allowClear
        />
      )
    }
    return (
      <Input ref={inputRef as any} value={value}
        onChange={e => { setValue(e.target.value); valueRef.current = e.target.value }}
        onPressEnter={handleSave} onBlur={handleSave} />
    )
  }

  return (
    <td {...restProps} onDoubleClick={editable ? handleDoubleClick : undefined} style={{ cursor: editable ? 'pointer' : 'default' }}>
      {editable && editingCell ? renderInput() : children}
    </td>
  )
}

const PriceManagement = () => {
  const [skus, setSkus] = useState<Sku[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [promotions, setPromotions] = useState<Promotion[]>([])
  const [loading, setLoading] = useState(false)
  const [historyModalVisible, setHistoryModalVisible] = useState(false)
  const [selectedSku, setSelectedSku] = useState<Sku | null>(null)
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([])
  const [searchKeyword, setSearchKeyword] = useState('')
  const [filterCategory, setFilterCategory] = useState<number | 'all'>('all')
  const [filterStockStatus, setFilterStockStatus] = useState<'all' | 'low' | 'out'>('all')
  const [activeTab, setActiveTab] = useState('price')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })

  const [stats, setStats] = useState({ totalSkus: 0, lowStockSkus: 0, outOfStockSkus: 0, avgPrice: 0 })

  useEffect(() => {
    fetchCategories()
    fetchPromotions()
  }, [])

  useEffect(() => {
    fetchSkus()
  }, [filterCategory, filterStockStatus, searchKeyword, pagination.current, pagination.pageSize])

  const fetchCategories = async () => {
    try {
      const res = await categoryAPI.getTree()
      if (res.code === 200) {
        const flatten = (list: any[]): Category[] => {
          const result: Category[] = []
          const traverse = (items: any[]) => { items.forEach(item => { result.push({ ...item, children: undefined }); if (item.children) traverse(item.children) }) }
          traverse(list); return result
        }
        setCategories(flatten(res.data || []))
      }
    } catch (error) { message.error('获取分类失败') }
  }

  const fetchPromotions = async () => {
    try {
      const res = await priceAPI.getPromotions()
      if (res.code === 200) setPromotions(res.data || [])
    } catch (error) {
      setPromotions([
        { id: 1, name: '买10送1', type: 'buyXgetY', discountValue: 1 },
        { id: 2, name: '满5袋95折', type: 'discount', discountValue: 95 },
        { id: 3, name: '满100减10', type: 'reduce', discountValue: 10 },
        { id: 4, name: '无促销', type: 'none', discountValue: 0 },
      ])
    }
  }

  const fetchSkus = async () => {
    setLoading(true)
    try {
      const params: any = { page: pagination.current, pageSize: pagination.pageSize, _t: Date.now() }
      if (searchKeyword) params.keyword = searchKeyword
      if (filterCategory !== 'all') params.categoryId = filterCategory
      if (filterStockStatus !== 'all') params.stockStatus = filterStockStatus

      const res = await priceAPI.getList(params)
      if (res.code === 200) {
        setSkus(res.data.list || [])
        setPagination(prev => ({ ...prev, total: res.data.total || 0 }))
        const list = res.data.list || []
        const prices = list.map((s: Sku) => s.price)
        setStats({
          totalSkus: res.data.total || list.length,
          lowStockSkus: list.filter((s: Sku) => s.stock > 0 && s.stock <= s.stockWarning).length,
          outOfStockSkus: list.filter((s: Sku) => s.stock === 0).length,
          avgPrice: prices.length > 0 ? prices.reduce((a: number, b: number) => a + b, 0) / prices.length : 0,
        })
      }
      setHasUnsavedChanges(false)
    } catch (error) { message.error('获取商品价格数据失败') }
    finally { setLoading(false) }
  }

  const handleCellSave = (record: Sku, dataIndex: string, value: any) => {
    const newSkus = skus.map(s => {
      if (s.id === record.id) {
        const updated = { ...s }
        if (!updated._modified) {
          updated._originalPrice = s.price
          updated._originalStock = s.stock
          updated._originalPromotionId = s.promotionId
          updated._originalProductionDate = s.productionDate
          updated._originalNearExpiry = s.nearExpiry
        }
        // 数字框被清空(null/undefined)时回退为原值，避免 toFixed 崩溃白屏
        if (dataIndex === 'price') updated.price = value === null || value === undefined ? s.price : value
        else if (dataIndex === 'stock') updated.stock = value === null || value === undefined ? s.stock : value
        else if (dataIndex === 'promotionId') { updated.promotionId = value; updated.promotionName = promotions.find(p => p.id === value)?.name || '无促销' }
        else if (dataIndex === 'productionDate') updated.productionDate = value || null
        else if (dataIndex === 'nearExpiry') updated.nearExpiry = value === true || value === 'true' || value === 1
        // 与原值逐项比较，全部一致则不标记为已修改
        const changed = updated.price !== updated._originalPrice
          || updated.stock !== updated._originalStock
          || (updated.promotionId ?? null) !== (updated._originalPromotionId ?? null)
          || (updated.productionDate ?? null) !== (updated._originalProductionDate ?? null)
          || !!updated.nearExpiry !== !!updated._originalNearExpiry
        if (!changed) return s
        updated._modified = true
        return updated
      }
      return s
    })
    setSkus(newSkus)
    setHasUnsavedChanges(true)
    updateStats(newSkus)
  }

  const updateStats = (data: Sku[]) => {
    const prices = data.map(s => s.price)
    setStats(prev => ({
      ...prev,
      totalSkus: prev.totalSkus || data.length,
      lowStockSkus: data.filter(s => s.stock > 0 && s.stock <= s.stockWarning).length,
      outOfStockSkus: data.filter(s => s.stock === 0).length,
      avgPrice: prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0,
    }))
  }

  const handleBatchSave = async () => {
    const modifiedSkus = skus.filter(s => s._modified)
    if (modifiedSkus.length === 0) { message.info('没有需要保存的更改'); return }

    confirmDanger({
      title: '确认批量保存',
      content: `确定要保存 ${modifiedSkus.length} 条修改记录吗？`,
      okText: '确认保存',
      okType: 'primary',
      module: '价格管理',
      action: '批量保存',
      targetName: `${modifiedSkus.length} 条SKU记录`,
      onOk: async () => {
        setSaving(true)
        try {
          const saveData = modifiedSkus.map(s => ({
            id: s.id,
            price: Number(s.price) || 0,
            stock: Number(s.stock) || 0,
            promotionId: s.promotionId || null,
            productionDate: s.productionDate || null,
            nearExpiry: s.nearExpiry ? 1 : 0
          }))
          const res = await priceAPI.batchSave(saveData)
          if (res.code === 200) {
            setHasUnsavedChanges(false)
            message.success(`成功保存 ${modifiedSkus.length} 条记录`)
            // 停留在当前页，重新拉取服务器最新数据
            await fetchSkus()
          }
        } finally { setSaving(false) }
      },
    })
  }

  const handleUndoAll = () => {
    const newSkus = skus.map(s => {
      if (s._modified) return { ...s, price: s._originalPrice ?? s.price, stock: s._originalStock ?? s.stock, promotionId: s._originalPromotionId ?? s.promotionId, promotionName: promotions.find(p => p.id === (s._originalPromotionId ?? s.promotionId))?.name || '无促销', productionDate: s._originalProductionDate ?? s.productionDate, nearExpiry: s._originalNearExpiry ?? s.nearExpiry, _modified: false }
      return s
    })
    setSkus(newSkus); setHasUnsavedChanges(false); updateStats(newSkus)
    message.info('已撤销所有更改')
  }

  const handleUndoItem = (record: Sku) => {
    const newSkus = skus.map(s => {
      if (s.id === record.id && s._modified) return { ...s, price: s._originalPrice ?? s.price, stock: s._originalStock ?? s.stock, promotionId: s._originalPromotionId ?? s.promotionId, promotionName: promotions.find(p => p.id === (s._originalPromotionId ?? s.promotionId))?.name || '无促销', productionDate: s._originalProductionDate ?? s.productionDate, nearExpiry: s._originalNearExpiry ?? s.nearExpiry, _modified: false }
      return s
    })
    setSkus(newSkus); setHasUnsavedChanges(newSkus.some(s => s._modified)); updateStats(newSkus)
    message.success('已撤销更改')
  }

  const handleViewHistory = async (record: Sku) => {
    setSelectedSku(record)
    try {
      const res = await priceAPI.getPriceHistory(record.id)
      if (res.code === 200) setPriceHistory(res.data || [])
    } catch (error) { setPriceHistory([]) }
    setHistoryModalVisible(true)
  }

  const handleSyncFromProduct = () => {
    confirmDanger({
      title: '确认同步',
      content: hasUnsavedChanges
        ? '您有未保存的更改，同步将丢失这些更改。是否继续？'
        : '确定要从商品管理同步最新数据吗？',
      okText: '确认同步',
      okType: 'primary',
      module: '价格管理',
      action: '同步商品数据',
      targetName: '价格与库存数据',
      onOk: async () => {
        try {
          await priceAPI.syncProducts()
          message.success('同步成功')
          setPagination(prev => ({ ...prev, current: 1 }))
        } catch (error) {
          message.error('同步失败')
        }
      },
    })
  }

  const handleSearch = () => {
    if (pagination.current === 1) {
      fetchSkus()
    } else {
      setPagination(prev => ({ ...prev, current: 1 }))
    }
  }

  const columns: ColumnsType<Sku> = [
    { title: 'SKU ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: '商品名称', dataIndex: 'productName', key: 'productName', width: 140 },
    { title: '规格', dataIndex: 'spec', key: 'spec', width: 110 },
    { title: '分类', dataIndex: 'categoryName', key: 'categoryName', width: 90 },
    {
      title: '价格', dataIndex: 'price', key: 'price', width: 120,
      render: (val: number, record: Sku) => {
        const isModified = record._modified && record._originalPrice !== record.price
        return (<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: isModified ? '#1890ff' : '#cf1322', fontWeight: 'bold', fontSize: 15 }}>¥{val.toFixed(2)}</span>
          {isModified && <Tooltip title={`原价：¥${record._originalPrice?.toFixed(2)}`}><Tag color="blue" style={{ fontSize: 10 }}>改</Tag></Tooltip>}
        </div>)
      },
      onCell: (record: Sku) => ({ record, editable: true, dataIndex: 'price', title: '价格', inputType: 'number', onSave: handleCellSave }),
    },
    {
      title: '库存', dataIndex: 'stock', key: 'stock', width: 100,
      render: (val: number, record: Sku) => {
        const isModified = record._modified && record._originalStock !== record.stock
        return (<div>
          <span style={{ color: isModified ? '#1890ff' : (val <= record.stockWarning ? '#fa8c16' : '#52c41a'), fontWeight: isModified ? 'bold' : 'normal' }}>{val}</span>
          {isModified && <Tag color="blue" style={{ fontSize: 10, marginLeft: 4 }}>改</Tag>}
          {!isModified && val === 0 && <Tag color="red" style={{ marginLeft: 4 }}>售罄</Tag>}
          {!isModified && val > 0 && val <= record.stockWarning && <Tag color="orange" style={{ marginLeft: 4 }}>紧张</Tag>}
        </div>)
      },
      onCell: (record: Sku) => ({ record, editable: true, dataIndex: 'stock', title: '库存', inputType: 'number', onSave: handleCellSave }),
    },
    {
      title: '促销方案', dataIndex: 'promotionName', key: 'promotionName', width: 120,
      render: (val: string, record: Sku) => {
        const isModified = record._modified && record._originalPromotionId !== record.promotionId
        return (<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Tag color={val === '无促销' ? 'default' : 'blue'}>{val}</Tag>{isModified && <Tag color="blue" style={{ fontSize: 10 }}>改</Tag>}</div>)
      },
      onCell: (record: Sku) => ({ record, editable: true, dataIndex: 'promotionId', title: '促销方案', inputType: 'select', options: promotions.map(p => ({ value: p.id, label: p.name })), onSave: handleCellSave }),
    },
    {
      title: '生产日期', dataIndex: 'productionDate', key: 'productionDate', width: 120,
      render: (val: string, record: Sku) => {
        const isModified = record._modified && record._originalProductionDate !== record.productionDate
        return (<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ color: isModified ? '#1890ff' : 'inherit' }}>{val || '-'}</span>{isModified && <Tag color="blue" style={{ fontSize: 10 }}>改</Tag>}</div>)
      },
      onCell: (record: Sku) => ({ record, editable: true, dataIndex: 'productionDate', title: '生产日期', inputType: 'date', onSave: handleCellSave }),
    },
    {
      title: '临保特卖', dataIndex: 'nearExpiry', key: 'nearExpiry', width: 90,
      render: (val: boolean, record: Sku) => {
        const isModified = record._modified && record._originalNearExpiry !== record.nearExpiry
        return (<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Tag color={val ? 'orange' : 'default'}>{val ? '是' : '否'}</Tag>
          {isModified && <Tag color="blue" style={{ fontSize: 10 }}>改</Tag>}
        </div>)
      },
      onCell: (record: Sku) => ({ record, editable: true, dataIndex: 'nearExpiry', title: '临保特卖', inputType: 'select', options: [{ value: true, label: '是' }, { value: false, label: '否' }], onSave: handleCellSave }),
    },
    {
      title: '到期日期', dataIndex: 'expiryDate', key: 'expiryDate', width: 110,
      render: (val: string) => val || '-',
    },
    {
      title: '操作', key: 'action', width: 100, fixed: 'right' as const,
      render: (_: any, record: Sku) => (
        <Space size="small">
          <Tooltip title="查看价格历史"><Button type="link" size="small" icon={<HistoryOutlined />} onClick={() => handleViewHistory(record)} /></Tooltip>
          {record._modified && <Tooltip title="撤销更改"><Button type="link" size="small" icon={<SyncOutlined />} onClick={() => handleUndoItem(record)} style={{ color: '#fa8c16' }} /></Tooltip>}
        </Space>
      ),
    },
  ]

  const historyColumns: ColumnsType<PriceHistory> = [
    { title: '原价格', dataIndex: 'oldPrice', key: 'oldPrice', render: (v: number) => `¥${v.toFixed(2)}` },
    { title: '新价格', dataIndex: 'newPrice', key: 'newPrice', render: (v: number) => `¥${v.toFixed(2)}` },
    { title: '变动时间', dataIndex: 'changeTime', key: 'changeTime' },
    { title: '操作人', dataIndex: 'operator', key: 'operator' },
  ]

  return (
    <PageErrorBoundary>
    <div className="price-management-page">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card size="small"><Statistic title="SKU总数" value={stats.totalSkus} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="库存预警" value={stats.lowStockSkus} valueStyle={{ color: '#fa8c16' }} prefix={<WarningOutlined />} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="售罄商品" value={stats.outOfStockSkus} valueStyle={{ color: '#cf1322' }} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="平均价格" value={stats.avgPrice} precision={2} prefix={<DollarOutlined />} suffix="元" /></Card></Col>
      </Row>

      <Card size="small">
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[{
          key: 'price',
          label: '价格与库存管理',
          children: (<>
            <Space wrap style={{ marginBottom: 16 }}>
              <Input placeholder="搜索商品名称/规格" prefix={<SearchOutlined />} style={{ width: 200 }} value={searchKeyword} onChange={e => setSearchKeyword(e.target.value)} onPressEnter={handleSearch} />
              <Select style={{ width: 150 }} value={filterCategory} onChange={setFilterCategory} allowClear placeholder="全部分类">
                <Option value="all">全部分类</Option>
                {categories.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
              </Select>
              <Select style={{ width: 120 }} value={filterStockStatus} onChange={setFilterStockStatus}>
                <Option value="all">全部库存</Option><Option value="low">库存紧张</Option><Option value="out">已售罄</Option>
              </Select>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
              <Button icon={<SyncOutlined />} onClick={handleSyncFromProduct}>同步商品数据</Button>
            </Space>

            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#e6f7ff', borderRadius: 4, fontSize: 13, color: '#666' }}>
              <EditOutlined style={{ marginRight: 8 }} />双击单元格可直接编辑。修改后点击「批量保存」提交。
            </div>

            {hasUnsavedChanges && (
              <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fff7e6', borderRadius: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #ffd591' }}>
                <Space><Tag color="warning" style={{ fontSize: 14, padding: '4px 12px' }}>您有 {skus.filter(s => s._modified).length} 条未保存的更改</Tag></Space>
                <Space><Button onClick={handleUndoAll}>撤销全部</Button><Button type="primary" icon={<SaveOutlined />} onClick={handleBatchSave} loading={saving}>批量保存</Button></Space>
              </div>
            )}

            <Table columns={columns} dataSource={skus} rowKey="id" loading={loading} scroll={{ x: 1400 }}
              pagination={{ ...pagination, showSizeChanger: true, showTotal: (total) => `共 ${total} 条`, onChange: (page, pageSize) => setPagination(prev => ({ ...prev, current: page, pageSize })) }}
              components={{ body: { cell: EditableCell } }} />
          </>)}
        ]} />
      </Card>

      <Modal title={`价格变动历史 - ${selectedSku?.productName} ${selectedSku?.spec}`} open={historyModalVisible} onCancel={() => setHistoryModalVisible(false)} footer={null} width={600}>
        <div style={{ marginBottom: 16 }}><span style={{ fontSize: 16, fontWeight: 'bold', color: '#cf1322' }}>当前价格：¥{(selectedSku?.price ?? 0).toFixed(2)}</span></div>
        <Table columns={historyColumns} dataSource={priceHistory} rowKey="id" pagination={false} size="small" />
      </Modal>
    </div>
    </PageErrorBoundary>
  )
}

export default PriceManagement
